import { useLocation, useSearchParams } from '@remix-run/react';
import { useCallback, useEffect, useRef } from 'react';

import { DEMO_TOUR_STEPS } from '~/config/demo-tour';

import 'intro.js/introjs.css';

type DemoTourProps = {
  enabled: boolean;
  tourRequested?: boolean;
};

function waitForElement(selector: string, timeoutMs = 8000): Promise<Element | null> {
  return new Promise(resolve => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const timer = window.setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeoutMs);
  });
}

export function DemoTour({ enabled, tourRequested }: DemoTourProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const startedRef = useRef(false);

  const startTour = useCallback(async () => {
    if (!enabled || startedRef.current) {
      return;
    }

    const { default: introJs } = await import('intro.js');
    const steps = [];

    for (const step of DEMO_TOUR_STEPS) {
      if (step.element) {
        const el = await waitForElement(step.element, 4000);
        if (!el) {
          continue;
        }
      }
      steps.push(step);
    }

    if (steps.length === 0) {
      return;
    }

    startedRef.current = true;

    const tour = introJs.tour();
    tour.setOptions({
      steps,
      showProgress: true,
      showBullets: false,
      exitOnOverlayClick: false,
      disableInteraction: false,
      nextLabel: 'Next',
      prevLabel: 'Back',
      doneLabel: 'Done',
    });
    tour.start();
  }, [enabled]);

  useEffect(() => {
    startedRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const shouldStart = enabled && (tourRequested || searchParams.get('tour') === '1');
    if (!shouldStart) {
      return;
    }

    const timer = window.setTimeout(() => {
      void startTour();
      if (searchParams.get('tour') === '1') {
        const next = new URLSearchParams(searchParams);
        next.delete('tour');
        setSearchParams(next, { replace: true });
      }
    }, 600);

    return () => window.clearTimeout(timer);
  }, [enabled, tourRequested, searchParams, setSearchParams, startTour]);

  return null;
}
